import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X, Layers } from "lucide-react";

export type ToastType = "success" | "error" | "warning" | "info";

export interface ToastMessage {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  count?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastContainerProps {
  toasts: ToastMessage[];
  onDismiss: (id: string) => void;
  onDismissAll?: () => void;
}

export default function ToastContainer({ toasts, onDismiss, onDismissAll }: ToastContainerProps) {
  return (
    <aside
      aria-live="polite"
      aria-atomic="true"
      aria-label="Notifications"
      className="fixed bottom-3 right-3 sm:bottom-4 sm:right-4 z-50 flex flex-col items-end gap-1.5 w-auto max-w-[280px] sm:max-w-[310px] pointer-events-none"
      id="toast-notification-region"
    >
      {/* Batch Clear Button if 2 or more toasts are active */}
      {toasts.length >= 2 && onDismissAll && (
        <motion.button
          initial={{ opacity: 0, y: 6, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          type="button"
          onClick={onDismissAll}
          className="pointer-events-auto self-end px-2 py-0.5 rounded-full bg-slate-900/85 dark:bg-slate-800/90 hover:bg-slate-950 text-slate-200 text-[10px] font-mono font-medium border border-slate-700/70 shadow-2xs backdrop-blur-md flex items-center gap-1 transition-all active:scale-95 cursor-pointer"
          title="Dismiss all active notifications"
        >
          <Layers className="w-2.5 h-2.5 text-slate-400" />
          <span>Dismiss all ({toasts.length})</span>
        </motion.button>
      )}

      <AnimatePresence mode="popLayout">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
        ))}
      </AnimatePresence>
    </aside>
  );
}

const ToastItem: React.FC<{
  toast: ToastMessage;
  onDismiss: (id: string) => void;
}> = ({ toast, onDismiss }) => {
  const duration = toast.duration ?? 2600;
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(100);
  const startTimeRef = useRef<number>(Date.now());
  const remainingTimeRef = useRef<number>(duration);

  // Smooth progress bar calculation with pause on hover/focus
  useEffect(() => {
    if (duration <= 0) return;

    remainingTimeRef.current = duration;
    startTimeRef.current = Date.now();
    setProgress(100);

    let animationFrameId: number;

    const tick = () => {
      if (!isPaused) {
        const elapsed = Date.now() - startTimeRef.current;
        const remaining = Math.max(0, remainingTimeRef.current - elapsed);
        const percent = Math.max(0, (remaining / duration) * 100);
        setProgress(percent);

        if (remaining <= 0) {
          onDismiss(toast.id);
          return;
        }
      }
      animationFrameId = requestAnimationFrame(tick);
    };

    animationFrameId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [toast.id, duration, isPaused, onDismiss]);

  const handleMouseEnter = () => {
    setIsPaused(true);
    const elapsed = Date.now() - startTimeRef.current;
    remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
  };

  const handleMouseLeave = () => {
    startTimeRef.current = Date.now();
    setIsPaused(false);
  };

  const config = {
    success: {
      icon: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0" aria-hidden="true" />,
      border: "border-emerald-200/90 dark:border-emerald-800/60",
      bg: "bg-white/95 dark:bg-slate-900/95 text-slate-900 dark:text-slate-100",
      progressBar: "bg-emerald-500/70 dark:bg-emerald-400/80",
      indicator: "bg-emerald-500",
      badge: "bg-emerald-100/80 dark:bg-emerald-950/80 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800"
    },
    error: {
      icon: <AlertCircle className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400 shrink-0" aria-hidden="true" />,
      border: "border-rose-200/90 dark:border-rose-800/60",
      bg: "bg-white/95 dark:bg-slate-900/95 text-slate-900 dark:text-slate-100",
      progressBar: "bg-rose-500/70 dark:bg-rose-400/80",
      indicator: "bg-rose-500",
      badge: "bg-rose-100/80 dark:bg-rose-950/80 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800"
    },
    warning: {
      icon: <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 shrink-0" aria-hidden="true" />,
      border: "border-amber-200/90 dark:border-amber-800/60",
      bg: "bg-white/95 dark:bg-slate-900/95 text-slate-900 dark:text-slate-100",
      progressBar: "bg-amber-500/70 dark:bg-amber-400/80",
      indicator: "bg-amber-500",
      badge: "bg-amber-100/80 dark:bg-amber-950/80 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800"
    },
    info: {
      icon: <Info className="w-3.5 h-3.5 text-slate-700 dark:text-slate-300 shrink-0" aria-hidden="true" />,
      border: "border-slate-300/90 dark:border-slate-700/60",
      bg: "bg-white/95 dark:bg-slate-900/95 text-slate-900 dark:text-slate-100",
      progressBar: "bg-slate-500/70 dark:bg-slate-400/80",
      indicator: "bg-slate-500",
      badge: "bg-slate-100/80 dark:bg-slate-800/80 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700"
    }
  }[toast.type];

  return (
    <motion.div
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.6}
      onDragEnd={(_, info) => {
        if (Math.abs(info.offset.x) > 40 || Math.abs(info.velocity.x) > 300) {
          onDismiss(toast.id);
        }
      }}
      initial={{ opacity: 0, y: 12, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, x: 40, scale: 0.9, transition: { duration: 0.15 } }}
      transition={{ type: "spring", stiffness: 450, damping: 30 }}
      role={toast.type === "error" || toast.type === "warning" ? "alert" : "status"}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
      onClick={() => onDismiss(toast.id)}
      title="Click or swipe to dismiss"
      className={`pointer-events-auto relative w-full shadow-sm hover:shadow-md rounded-xl border p-2 sm:p-2.5 backdrop-blur-md select-none touch-pan-y transition-all cursor-pointer group hover:opacity-90 active:scale-98 ${config.border} ${config.bg} overflow-hidden`}
    >
      {/* Visual Accent Edge Stripe */}
      <div className={`absolute top-0 bottom-0 left-0 w-0.5 ${config.indicator}`} />

      <div className="flex items-center gap-2 pl-1.5 pr-0.5">
        <div className="shrink-0">{config.icon}</div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <h5 className="font-semibold text-xs leading-snug text-slate-900 dark:text-white truncate">
              {toast.title}
            </h5>
            
            {/* Repeated occurrence counter badge */}
            {toast.count && toast.count > 1 && (
              <span className={`px-1 py-0.2 rounded-full text-[9px] font-mono font-bold border ${config.badge}`}>
                &times;{toast.count}
              </span>
            )}
          </div>

          {toast.message && (
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 leading-tight font-normal line-clamp-1">
              {toast.message}
            </p>
          )}

          {toast.action && (
            <div className="mt-1.5">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toast.action?.onClick();
                  onDismiss(toast.id);
                }}
                className="px-2 py-0.5 text-[10px] font-bold rounded-md bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 text-white dark:text-slate-900 transition-all cursor-pointer shadow-2xs active:scale-95"
              >
                {toast.action.label}
              </button>
            </div>
          )}
        </div>

        {/* Quick Dismiss Button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(toast.id);
          }}
          aria-label="Dismiss notification"
          title="Dismiss"
          className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 p-1 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer shrink-0 -mr-0.5"
        >
          <X className="w-3 h-3" aria-hidden="true" />
        </button>
      </div>

      {/* Elapsed Progress Bar */}
      {duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-200/40 dark:bg-slate-800/60 overflow-hidden">
          <div
            className={`h-full ${config.progressBar} transition-all duration-75`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </motion.div>
  );
};
